import React from 'react';
import { Icon, Tooltip } from 'antd';
import { PhaseTitleContainer } from './BudgetStyles';
import {
  checkPreviousPhasesCompleted,
  getMissingColor,
  getMissingValue,
  getPhaseDate,
  phaseDatePassed,
  lessThanFifteenDaysToGo,
} from './budgetUtils';
import { getPhaseText } from './paymentPhasesTexts';
import { TranslateFromObject } from '../../infra/services/translations/AvailableTranslations';

const PaymentPhasesBudget = ({
  paymentPhases,
  weddingInfo,
  translate,
  activeLanguage,
  renderPrice,
  weddingDateHasPassed,
  ifPhaseComplete,
  openContract,
}) => {
  const percentageCalc4 = weddingInfo?.percentageCalc4 || 90;
  const isPhase4Full = percentageCalc4 === 100 && paymentPhases?.length > 4;

  const t = getPhaseText(activeLanguage);
  const phaseLabel = (order) => `${translate('PHASE')} ${order}`;
  const translateObject = (object) => TranslateFromObject(object, activeLanguage?.code);

  const [collapsedPhases, setCollapsedPhases] = React.useState(new Set());
  const togglePhase = (order) => setCollapsedPhases(prev => {
    const next = new Set(prev);
    next.has(order) ? next.delete(order) : next.add(order);
    return next;
  });

  const renderPhaseValueGrid = (previstoContent, pagoContent, faltaContent, missingColor, disclaimer, previstoLabel, _showCopyIcon, previstoTooltip, pagoTooltip, faltaTooltip, extraColumn = null) => (
    <div style={{ width: '100%', marginTop: '8px', marginBottom: '8px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: extraColumn ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', border: '1px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden', fontSize: '13px' }}>
        <div style={{ background: '#f5f5f5', padding: '3px 8px', fontSize: '7px', fontWeight: '600', color: '#888', borderRight: '1px solid #e8e8e8', textTransform: 'none', letterSpacing: '0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <span style={{ fontSize: '13px' }}>{translate('EXPECTED')}{previstoLabel ? ` (${previstoLabel})` : ''}</span>
          {previstoTooltip && (
            <Tooltip title={previstoTooltip} overlayStyle={{ maxWidth: 400 }}>
              <Icon type="exclamation-circle" style={{ marginLeft: 5, fontSize: 11, cursor: 'pointer', color: '#aaa', fontWeight: 'normal', textTransform: 'none' }} />
            </Tooltip>
          )}
        </div>
        <div style={{ background: '#f5f5f5', padding: '3px 8px', fontSize: '7px', fontWeight: '600', color: '#888', borderRight: '1px solid #e8e8e8', textTransform: 'none', letterSpacing: '0', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '13px' }}>{translate('PHASE_PAID')}</span>
          {pagoTooltip && (
            <Tooltip title={pagoTooltip} overlayStyle={{ maxWidth: 400 }}>
              <Icon type="exclamation-circle" style={{ fontSize: 11, cursor: 'pointer', color: '#aaa', fontWeight: 'normal', textTransform: 'none' }} />
            </Tooltip>
          )}
        </div>
        <div style={{ background: '#f5f5f5', padding: '3px 8px', fontSize: '7px', fontWeight: '600', color: missingColor !== 'inherit' ? missingColor : '#888', textTransform: 'none', letterSpacing: '0', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', ...(extraColumn ? { borderRight: '1px solid #e8e8e8' } : {}) }}>
          <span style={{ fontSize: '13px' }}>{translate('MISSING')}</span>
          {faltaTooltip && (
            <Tooltip title={faltaTooltip} overlayStyle={{ maxWidth: 400 }}>
              <Icon type="exclamation-circle" style={{ fontSize: 11, cursor: 'pointer', color: missingColor !== 'inherit' ? missingColor : '#aaa', fontWeight: 'normal', textTransform: 'none' }} />
            </Tooltip>
          )}
        </div>
        {extraColumn && (
          <div style={{ background: '#f5f5f5', padding: '3px 8px', fontSize: '7px', fontWeight: '600', color: extraColumn.color || '#888', textTransform: 'none', letterSpacing: '0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <span style={{ fontSize: '13px' }}>{extraColumn.header}</span>
            {extraColumn.tooltip && (
              <Tooltip title={extraColumn.tooltip} overlayStyle={{ maxWidth: 400 }}>
                <Icon type="exclamation-circle" style={{ marginLeft: 5, fontSize: 11, cursor: 'pointer', color: '#aaa', fontWeight: 'normal', textTransform: 'none' }} />
              </Tooltip>
            )}
          </div>
        )}
        <div style={{ padding: '5px 8px', borderRight: '1px solid #e8e8e8', borderTop: '1px solid #e8e8e8', lineHeight: '1.5', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>{previstoContent}</div>
        <div style={{ padding: '5px 8px', borderRight: '1px solid #e8e8e8', borderTop: '1px solid #e8e8e8', lineHeight: '1.5', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>{pagoContent}</div>
        <div style={{ padding: '5px 8px', borderTop: '1px solid #e8e8e8', lineHeight: '1.5', color: missingColor, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', ...(extraColumn ? { borderRight: '1px solid #e8e8e8' } : {}) }}>{faltaContent}</div>
        {extraColumn && (
          <div style={{ padding: '5px 8px', borderTop: '1px solid #e8e8e8', lineHeight: '1.5', color: extraColumn.color, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
            {extraColumn.content}
          </div>
        )}
      </div>
      {disclaimer && (
        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px', lineHeight: '1.4', textAlign: 'justify' }}>{disclaimer}</div>
      )}
    </div>
  );

  const tooltipRow = (label, value, formula) => (
    <div style={{ marginTop: formula ? 5 : 2 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ color: '#ccc' }}>{label}</span>
        <b>{value}</b>
      </div>
      {formula && (
        <div style={{ fontSize: 10, color: 'inherit', marginTop: 1, textAlign: 'right' }}>{formula}</div>
      )}
    </div>
  );

  const renderPhaseTitle = (p) => (
    <PhaseTitleContainer place={weddingInfo?.wedding_place} style={{ width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4, paddingBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon
          type={collapsedPhases.has(p?.order) ? 'right' : 'down'}
          style={{ cursor: 'pointer', color: '#aaa', fontSize: 12, flexShrink: 0 }}
          onClick={() => togglePhase(p?.order)}
        />
        <div>
          <b>{translateObject(p?.phase)} {p?.order || ''}: </b>
          <span>{getPhaseDate(p, paymentPhases, weddingInfo, translate)}</span>
        </div>
      </div>
      {(p?.order === 1 || p?.order === 2) && weddingInfo?.contractFile && ifPhaseComplete && !ifPhaseComplete(p?.order) && (
        <Tooltip title={translate('CHECK_CONTRACT_CONDITIONS')}>
          <Icon
            type="file-text"
            style={{ color: 'red', cursor: 'pointer', fontSize: 14, flexShrink: 0 }}
            onClick={() => openContract()}
          />
        </Tooltip>
      )}
    </PhaseTitleContainer>
  );

  const renderPhaseQuestions = (p) => {
    if (p?.order > 2 && p?.paymentQuestion) {
      return (
        <div className='subtitle'>
          {p?.paymentQuestion?.option?.title && (
            <span>
              <Icon type='question-circle' />
              {translateObject(p?.paymentQuestion?.option?.title)}
            </span>
          )}
        </div>
      );
    }
  };

  const renderPhase1Values = (p) => {
    if (p?.order !== 1) return null;
    const effectivePayed = (p?.payed || 0) + (p?.carryForward || 0);
    const displayedPago = Math.min(effectivePayed, p?.toPayWithPercentage || 0);
    const missingAmount = Math.max(0, (p?.toPayWithPercentage || 0) - effectivePayed);
    const missingColor = getMissingColor(p, missingAmount > 0);
    const excedente = Math.max(0, Number((effectivePayed - (p?.toPayWithPercentage || 0)).toFixed(2)));

    const extraColumn = {
      header: translate('EXCESS'),
      content: <div><b>{renderPrice(excedente)}</b></div>,
      color: '#888',
      tooltip: (
        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
          <div>{translate('CARRY_FORWARD_PAYMENT_VALUES')}</div>
          <div style={{ marginTop: 6, color: '#ddd' }}>{translate('CALCULATION')}:</div>
          <div style={{ paddingLeft: 8, color: '#ccc', fontSize: 11 }}>
            {translate('CASH_PAYMENT')} ({renderPrice(effectivePayed)}) - {translate('EXPECTED')} ({renderPrice(p?.toPayWithPercentage || 0)}) = {renderPrice(excedente)}
          </div>
        </div>
      ),
    };

    const pagoTooltip1 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('PAID')} - {translate('PHASE')} 1</div>
        <div style={{ marginBottom: 8 }}>
          {t('PAID_DESC_FIRST', { next: phaseLabel(2) })}
        </div>
        {p?.carryForward > 0 && tooltipRow(t('ROW_CARRIED_FROM_PREVIOUS'), renderPrice(p?.carryForward || 0))}
        {tooltipRow(t('ROW_PAYMENTS_THIS_PHASE'), renderPrice(p?.payed || 0))}
        {tooltipRow(t('ROW_EFFECTIVE_TOTAL'), renderPrice(effectivePayed), p?.carryForward > 0 ? <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</> : null)}
      </div>
    );
    const faltaTooltip1 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('MISSING')} - {translate('PHASE')} 1</div>
        <div style={{ marginBottom: 8 }}>
          {t('MISSING_DESC_PHASE')}
        </div>
        {tooltipRow(translate('EXPECTED'), renderPrice(p?.toPayWithPercentage || 0))}
        {tooltipRow(translate('CASH_PAYMENT'), renderPrice(effectivePayed))}
        {tooltipRow(translate('MISSING'), renderPrice(missingAmount), <>{renderPrice(p?.toPayWithPercentage || 0)} - {renderPrice(effectivePayed)}</>)}
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_COLOR_RULES')}
        </div>
      </div>
    );

    const previstoContent = <div><b>{renderPrice(p?.toPayWithPercentage || 0)}</b></div>;
    const pagoContent = (
      <>
        <div><b>{renderPrice(displayedPago)}</b></div>
        {p?.carryForward > 0 && (
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
            {translate('INCLUDES')} {renderPrice(p?.carryForward)} {translate('CARRIED_OVER')}
          </div>
        )}
      </>
    );
    const faltaContent = <div><b>{renderPrice(missingAmount)}</b></div>;

    return renderPhaseValueGrid(
      previstoContent, pagoContent, faltaContent, missingColor, null, null, false,
      t('DISCLAIMER_FIXED_YEAR_VALUE'),
      pagoTooltip1,
      faltaTooltip1,
      null
    );
  };

  const renderPhase2Values = (p) => {
    if (p?.order !== 2) return null;
    const effectivePayed = (p?.payed || 0) + (p?.carryForward || 0);
    const displayedPago = Math.min(effectivePayed, p?.toPayWithPercentage || 0);
    const missingAmount = Math.max(0, (p?.toPayWithPercentage || 0) - effectivePayed);
    const missingColor = getMissingColor(p, missingAmount > 0);
    const excedente = Math.max(0, Number((effectivePayed - (p?.toPayWithPercentage || 0)).toFixed(2)));

    const extraColumn = {
      header: translate('EXCESS'),
      content: <div><b>{renderPrice(excedente)}</b></div>,
      color: '#888',
      tooltip: (
        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
          <div>{translate('CARRY_FORWARD_PAYMENT_VALUES')}</div>
          <div style={{ marginTop: 6, color: '#ddd' }}>{translate('CALCULATION')}:</div>
          <div style={{ paddingLeft: 8, color: '#ccc', fontSize: 11 }}>
            {translate('CASH_PAYMENT')} ({renderPrice(effectivePayed)}) - {translate('EXPECTED')} ({renderPrice(p?.toPayWithPercentage || 0)}) = {renderPrice(excedente)}
          </div>
        </div>
      ),
    };

    const pagoTooltip2 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('PAID')} - {translate('PHASE')} 2</div>
        <div style={{ marginBottom: 8 }}>
          {t('PAID_DESC_CARRY', { prev: phaseLabel(1), next: phaseLabel(3) })}
        </div>
        {p?.carryForward > 0 && tooltipRow(t('ROW_CARRIED_FROM_PHASE', { phase: phaseLabel(1) }), renderPrice(p?.carryForward || 0))}
        {tooltipRow(t('ROW_PAYMENTS_THIS_PHASE'), renderPrice(p?.payed || 0))}
        {tooltipRow(t('ROW_EFFECTIVE_TOTAL'), renderPrice(effectivePayed), p?.carryForward > 0 ? <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</> : null)}
      </div>
    );
    const faltaTooltip2 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('MISSING')} - {translate('PHASE')} 2</div>
        <div style={{ marginBottom: 8 }}>
          {t('MISSING_DESC_PHASE')}
        </div>
        {tooltipRow(translate('EXPECTED'), renderPrice(p?.toPayWithPercentage || 0))}
        {tooltipRow(translate('CASH_PAYMENT'), renderPrice(effectivePayed))}
        {tooltipRow(translate('MISSING'), renderPrice(missingAmount), <>{renderPrice(p?.toPayWithPercentage || 0)} - {renderPrice(effectivePayed)}</>)}
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_COLOR_RULES')}
        </div>
      </div>
    );

    const previstoContent = <div><b>{renderPrice(p?.toPayWithPercentage || 0)}</b></div>;
    const pagoContent = (
      <>
        <div><b>{renderPrice(displayedPago)}</b></div>
        {p?.carryForward > 0 && (
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
            {translate('INCLUDES')} {renderPrice(p?.carryForward)} {translate('CARRIED_OVER')}
          </div>
        )}
      </>
    );
    const faltaContent = <div><b>{renderPrice(missingAmount)}</b></div>;

    return renderPhaseValueGrid(
      previstoContent, pagoContent, faltaContent, missingColor, null, null, false,
      t('DISCLAIMER_FIXED_YEAR_VALUE'),
      pagoTooltip2,
      faltaTooltip2,
      null
    );
  };

  const renderPhase3Values = (p) => {
    if (p?.order !== 3) return null;

    const effectivePayed = (p?.payed || 0) + (p?.carryForward || 0);
    const displayedPago = Math.min(effectivePayed, p?.toPayWithPercentage || 0);
    // Portal dos noivos: nas fases 3 e 4 apresenta-se apenas o valor já com a margem
    // incluída (nunca as percentagens nem o valor base sem margem).
    const missingAmount = getMissingValue(p, true);
    const missingColor = getMissingColor(p, missingAmount > 0);
    const excedente = Math.max(0, Number((effectivePayed - (p?.toPayWithPercentage || 0)).toFixed(2)));

    const extraColumn = {
      header: translate('EXCESS'),
      content: <div><b>{renderPrice(excedente)}</b></div>,
      color: '#888',
      tooltip: (
        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
          <div>{translate('CARRY_FORWARD_PAYMENT_VALUES')}</div>
          <div style={{ marginTop: 6, color: '#ddd' }}>{translate('CALCULATION')}:</div>
          <div style={{ paddingLeft: 8, color: '#ccc', fontSize: 11 }}>
            {translate('CASH_PAYMENT')} ({renderPrice(effectivePayed)}) - {translate('EXPECTED')} ({renderPrice(p?.toPayWithPercentage || 0)}) = {renderPrice(excedente)}
          </div>
        </div>
      ),
    };

    const prevPh1_3 = paymentPhases.find(ph => ph?.order === 1);
    const prevPh2_3 = paymentPhases.find(ph => ph?.order === 2);
    const dispPago1_3 = Math.min((prevPh1_3?.payed || 0) + (prevPh1_3?.carryForward || 0), prevPh1_3?.toPayWithPercentage || 0);
    const dispPago2_3 = Math.min((prevPh2_3?.payed || 0) + (prevPh2_3?.carryForward || 0), prevPh2_3?.toPayWithPercentage || 0);
    const totalPagoAnt3 = Number((dispPago1_3 + dispPago2_3).toFixed(2));
    const carryForwardFormula3 = p?.carryForward > 0
      ? <>{renderPrice(prevPh2_3?.payed || 0)} - {renderPrice(prevPh2_3?.toPay || 0)}</>
      : null;

    const previstoTooltip3 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('EXPECTED')} - {translate('PHASE')} 3</div>
        <div style={{ marginBottom: 8 }}>
          {t('EXPECTED_DESC_NO_PCT')}
        </div>
        <div style={{ marginBottom: 4, color: '#ddd', marginTop: 6 }}>{translate('PAID_BEFORE_THIS_PHASE')}:</div>
        {tooltipRow(`• ${phaseLabel(1)}`, renderPrice(dispPago1_3))}
        {tooltipRow(`• ${phaseLabel(2)}`, renderPrice(dispPago2_3))}
        {tooltipRow(t('ROW_TOTAL_PAID'), renderPrice(totalPagoAnt3), <>{renderPrice(dispPago1_3)} + {renderPrice(dispPago2_3)}</>)}
        {tooltipRow(t('ROW_EXPECTED_FOR_PHASE'), renderPrice(p?.toPayWithPercentage || 0))}
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_FIXED_VALUE_OVERRIDE', { phase: phaseLabel(3) })}
        </div>
      </div>
    );

    const pagoTooltip3 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('PAID')} - {translate('PHASE')} 3</div>
        <div style={{ marginBottom: 8 }}>
          {t('PAID_DESC_CARRY', { prev: phaseLabel(2), next: phaseLabel(4) })}
        </div>
        {tooltipRow(t('ROW_CARRIED_FROM_PHASE', { phase: phaseLabel(2) }), renderPrice(p?.carryForward || 0), carryForwardFormula3)}
        {tooltipRow(t('ROW_PAYMENTS_THIS_PHASE'), renderPrice(p?.payed || 0))}
        {tooltipRow(t('ROW_EFFECTIVE_TOTAL'), renderPrice(effectivePayed), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
      </div>
    );

    const faltaTooltip3 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('MISSING')} - {translate('PHASE')} 3</div>
        <div style={{ marginBottom: 8 }}>
          {t('MISSING_DESC_PHASE')}
        </div>
        {tooltipRow(translate('EXPECTED'), renderPrice(p?.toPayWithPercentage || 0))}
        {tooltipRow(translate('CASH_PAYMENT'), renderPrice(effectivePayed), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
        {tooltipRow(translate('MISSING'), renderPrice(missingAmount), <>{renderPrice(p?.toPayWithPercentage || 0)} - {renderPrice(effectivePayed)}</>)}
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_MISSING_RULES')}
        </div>
      </div>
    );

    const previstoContent = <div><b>{renderPrice(p?.toPayWithPercentage || 0)}</b></div>;

    const pagoContent = (
      <>
        <div><b>{renderPrice(displayedPago)}</b></div>
        {p?.carryForward > 0 && (
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
            {translate('INCLUDES')} {renderPrice(p?.carryForward)} {translate('CARRIED_OVER')}
          </div>
        )}
      </>
    );

    const faltaContent = <div><b>{renderPrice(missingAmount)}</b></div>;

    return renderPhaseValueGrid(
      previstoContent, pagoContent, faltaContent, missingColor, null,
      null,
      false,
      previstoTooltip3,
      pagoTooltip3,
      faltaTooltip3,
      null
    );
  };

  const renderPhase4Values = (p) => {
    if (p?.order !== 4) return null;

    const disclaimer = t('DISCLAIMER_ADJUST_PHASE', { phase: phaseLabel(3) });

    if (paymentPhases.length > 4) {
      const effectivePayed = (p?.payed || 0) + (p?.carryForward || 0);
      const displayedPago = Math.min(effectivePayed, p?.toPayWithPercentage || 0);
      // Portal dos noivos: nas fases 3 e 4 apresenta-se apenas o valor já com a margem
      // incluída (nunca as percentagens nem o valor base sem margem).
      const missingAmount = getMissingValue(p, true);
      const missingColor = getMissingColor(p, missingAmount > 0);
      const excedente = Math.max(0, Number((effectivePayed - (p?.toPayWithPercentage || 0)).toFixed(2)));

      const extraColumn = {
        header: translate('EXCESS'),
        content: <div><b>{renderPrice(excedente)}</b></div>,
        color: '#888',
        tooltip: (
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div>{translate('CARRY_FORWARD_PAYMENT_VALUES')}</div>
            <div style={{ marginTop: 6, color: '#ddd' }}>{translate('CALCULATION')}:</div>
            <div style={{ paddingLeft: 8, color: '#ccc', fontSize: 11 }}>
              {translate('CASH_PAYMENT')} ({renderPrice(effectivePayed)}) - {translate('EXPECTED')} ({renderPrice(p?.toPayWithPercentage || 0)}) = {renderPrice(excedente)}
            </div>
          </div>
        ),
      };

      const prevPh1_4 = paymentPhases.find(ph => ph?.order === 1);
      const prevPh2_4 = paymentPhases.find(ph => ph?.order === 2);
      const prevPh3_4 = paymentPhases.find(ph => ph?.order === 3);
      const prevPh3_4Effective = (prevPh3_4?.payed || 0) + (prevPh3_4?.carryForward || 0);
      const dispPago1_4 = Math.min((prevPh1_4?.payed || 0) + (prevPh1_4?.carryForward || 0), prevPh1_4?.toPayWithPercentage || 0);
      const dispPago2_4 = Math.min((prevPh2_4?.payed || 0) + (prevPh2_4?.carryForward || 0), prevPh2_4?.toPayWithPercentage || 0);
      const dispPago3_4 = Math.min(prevPh3_4Effective, prevPh3_4?.toPayWithPercentage || 0);
      const totalPagoAnt4 = Number((dispPago1_4 + dispPago2_4 + dispPago3_4).toFixed(2));
      const carryForwardFormula4 = p?.carryForward > 0
        ? <>{renderPrice(prevPh3_4Effective)} - {renderPrice(prevPh3_4?.toPayWithPercentage || 0)}</>
        : null;

      const previstoTooltip4 = (
        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('EXPECTED')} - {translate('PHASE')} 4</div>
          <div style={{ marginBottom: 8 }}>
            {t('EXPECTED_DESC_NO_PCT')}
          </div>
          <div style={{ marginBottom: 4, color: '#ddd', marginTop: 6 }}>{translate('PAID_BEFORE_THIS_PHASE')}:</div>
          {tooltipRow(`• ${phaseLabel(1)}`, renderPrice(dispPago1_4))}
          {tooltipRow(`• ${phaseLabel(2)}`, renderPrice(dispPago2_4))}
          {tooltipRow(`• ${phaseLabel(3)}`, renderPrice(dispPago3_4))}
          {tooltipRow(t('ROW_TOTAL_PAID'), renderPrice(totalPagoAnt4), <>{renderPrice(dispPago1_4)} + {renderPrice(dispPago2_4)} + {renderPrice(dispPago3_4)}</>)}
          {tooltipRow(t('ROW_EXPECTED_FOR_PHASE'), renderPrice(p?.toPayWithPercentage || 0))}
          <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
            {t('FOOTNOTE_ADJUST_PHASE_COMPLETE', { phase: phaseLabel(3) })}
          </div>
        </div>
      );

      const pagoTooltip4 = (
        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('PAID')} - {translate('PHASE')} 4</div>
          <div style={{ marginBottom: 8 }}>
            {t('PAID_DESC_CARRY', { prev: phaseLabel(3), next: phaseLabel(5) })}
          </div>
          {tooltipRow(t('ROW_CARRIED_FROM_PHASE', { phase: phaseLabel(3) }), renderPrice(p?.carryForward || 0), carryForwardFormula4)}
          {tooltipRow(t('ROW_PAYMENTS_THIS_PHASE'), renderPrice(p?.payed || 0))}
          {tooltipRow(t('ROW_EFFECTIVE_TOTAL'), renderPrice(effectivePayed), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
        </div>
      );

      const faltaTooltip4 = (
        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('MISSING')} - {translate('PHASE')} 4</div>
          <div style={{ marginBottom: 8 }}>
            {t('MISSING_DESC_PHASE')}
          </div>
          {tooltipRow(translate('EXPECTED'), renderPrice(p?.toPayWithPercentage || 0))}
          {tooltipRow(translate('CASH_PAYMENT'), renderPrice(effectivePayed), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
          {tooltipRow(translate('MISSING'), renderPrice(missingAmount), <>{renderPrice(p?.toPayWithPercentage || 0)} - {renderPrice(effectivePayed)}</>)}
          <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
            {t('FOOTNOTE_MISSING_RULES')}
          </div>
        </div>
      );

      const previstoContent = <div><b>{renderPrice(p?.toPayWithPercentage || 0)}</b></div>;

      const pagoContent = (
        <>
          <div><b>{renderPrice(displayedPago)}</b></div>
          {p?.carryForward > 0 && (
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              {translate('INCLUDES')} {renderPrice(p?.carryForward)} {translate('CARRIED_OVER')}
            </div>
          )}
        </>
      );

      const faltaContent = <div><b>{renderPrice(missingAmount)}</b></div>;

      return renderPhaseValueGrid(
        previstoContent, pagoContent, faltaContent, missingColor, disclaimer,
        null,
        false,
        previstoTooltip4,
        pagoTooltip4,
        faltaTooltip4,
        null
      );
    }

    const effectivePago = (p?.payed || 0) + (p?.carryForward || 0);
    const missingAmount = Number(((p?.toPay || 0) - effectivePago).toFixed(2));
    const missingColor = getMissingColor(p, missingAmount > 0);

    const prevPh1_f4 = paymentPhases.find(ph => ph?.order === 1);
    const prevPh2_f4 = paymentPhases.find(ph => ph?.order === 2);
    const prevPh3_f4 = paymentPhases.find(ph => ph?.order === 3);
    const prevPh3_f4Effective = (prevPh3_f4?.payed || 0) + (prevPh3_f4?.carryForward || 0);
    const dispPago1_f4 = Math.min((prevPh1_f4?.payed || 0) + (prevPh1_f4?.carryForward || 0), prevPh1_f4?.toPayWithPercentage || 0);
    const dispPago2_f4 = Math.min((prevPh2_f4?.payed || 0) + (prevPh2_f4?.carryForward || 0), prevPh2_f4?.toPayWithPercentage || 0);
    const dispPago3_f4 = Math.min(prevPh3_f4Effective, prevPh3_f4?.toPayWithPercentage || 0);
    const totalPagoAntF4 = Number((dispPago1_f4 + dispPago2_f4 + dispPago3_f4).toFixed(2));
    const totalOrcamentoF4 = Number((totalPagoAntF4 + (p?.totalMissing || 0)).toFixed(2));
    const carryForwardFormulaFinal4 = p?.carryForward > 0
      ? <>{renderPrice(prevPh3_f4Effective)} - {renderPrice(prevPh3_f4?.toPayWithPercentage || 0)}</>
      : null;

    const previstoTooltipF4 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('EXPECTED')} - {translate('PHASE')} 4 ({translate('FINAL_PHASE')})</div>
        <div style={{ marginBottom: 8 }}>
          {t('EXPECTED_DESC_FINAL', { prev: phaseLabel(3) })}
        </div>
        <div style={{ marginBottom: 4, color: '#ddd', marginTop: 6 }}>{translate('PAID_BEFORE_THIS_PHASE')}:</div>
        {tooltipRow(`• ${phaseLabel(1)}`, renderPrice(dispPago1_f4))}
        {tooltipRow(`• ${phaseLabel(2)}`, renderPrice(dispPago2_f4))}
        {tooltipRow(`• ${phaseLabel(3)}`, renderPrice(dispPago3_f4))}
        {tooltipRow(t('ROW_TOTAL_PAID'), renderPrice(totalPagoAntF4), <>{renderPrice(dispPago1_f4)} + {renderPrice(dispPago2_f4)} + {renderPrice(dispPago3_f4)}</>)}
        {tooltipRow(t('ROW_OUTSTANDING_BASE'), renderPrice(p?.totalMissing || 0), <>{renderPrice(totalOrcamentoF4)} − {renderPrice(totalPagoAntF4)}</>)}
        {tooltipRow(t('ROW_VALUE_WITH_MARGIN'), renderPrice(p?.toPayWithPercentage || 0))}
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_ADJUST_PHASE', { phase: phaseLabel(3) })}
        </div>
      </div>
    );

    const pagoTooltipF4 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('PAID')} - {translate('PHASE')} 4 ({translate('FINAL_PHASE')})</div>
        <div style={{ marginBottom: 8 }}>
          {t('PAID_DESC_FINAL', { prev: phaseLabel(3) })}
        </div>
        {tooltipRow(t('ROW_CARRIED_FROM_PHASE', { phase: phaseLabel(3) }), renderPrice(p?.carryForward || 0), carryForwardFormulaFinal4)}
        {tooltipRow(t('ROW_PAYMENTS_THIS_PHASE'), renderPrice(p?.payed || 0))}
        {tooltipRow(t('ROW_EFFECTIVE_TOTAL'), renderPrice(effectivePago), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
      </div>
    );

    const faltaTooltipF4 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('MISSING')} - {translate('PHASE')} 4 ({translate('FINAL_PHASE')})</div>
        <div style={{ marginBottom: 8 }}>
          {t('MISSING_DESC_TOTAL')}
        </div>
        {tooltipRow(translate('EXPECTED'), renderPrice(p?.toPay || 0))}
        {tooltipRow(translate('CASH_PAYMENT'), renderPrice(effectivePago), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
        <div style={{ marginTop: 5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ color: '#ccc' }}>{translate('MISSING')}</span>
            <b style={{ color: missingAmount < 0 ? '#52c41a' : 'inherit' }}>
              {missingAmount < 0 && '-'}{renderPrice(Math.abs(missingAmount))}
            </b>
          </div>
          <div style={{ fontSize: 10, color: 'inherit', marginTop: 1, textAlign: 'right' }}>
            {renderPrice(p?.toPay || 0)} - {renderPrice(effectivePago)}
          </div>
        </div>
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_COLOR_RULES_EXCESS')}
        </div>
      </div>
    );

    const previstoContent = <div><b>{renderPrice(p?.toPayWithPercentage || 0)}</b></div>;
    const pagoContent = (
      <>
        <div><b>{renderPrice(effectivePago)}</b></div>
        {p?.carryForward > 0 && (
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
            {translate('INCLUDES')} {renderPrice(p?.carryForward)} {translate('CARRIED_OVER')}
          </div>
        )}
      </>
    );
    const faltaContent = <div><b>{renderPrice(Math.max(0, missingAmount))}</b></div>;

    return renderPhaseValueGrid(
      previstoContent, pagoContent, faltaContent, missingColor, disclaimer,
      null,
      false,
      previstoTooltipF4,
      pagoTooltipF4,
      faltaTooltipF4,
      null
    );
  };

  const renderPhase5Values = (p) => {
    if (p?.order <= 4 || isPhase4Full) return null;

    const prevComplete = checkPreviousPhasesCompleted(p, paymentPhases);
    const effectivePago = (p?.payed || 0) + (p?.carryForward || 0);
    const missingAmount = Number(((p?.toPay || 0) - effectivePago).toFixed(2));
    const missingColor = getMissingColor(p, missingAmount > 0);

    const prevPh1_5 = paymentPhases.find(ph => ph?.order === 1);
    const prevPh2_5 = paymentPhases.find(ph => ph?.order === 2);
    const prevPh3_5 = paymentPhases.find(ph => ph?.order === 3);
    const prevPh4_5 = paymentPhases.find(ph => ph?.order === 4);
    const prevPh4_5Effective = (prevPh4_5?.payed || 0) + (prevPh4_5?.carryForward || 0);
    const dispPago1_5 = Math.min((prevPh1_5?.payed || 0) + (prevPh1_5?.carryForward || 0), prevPh1_5?.toPayWithPercentage || 0);
    const dispPago2_5 = Math.min((prevPh2_5?.payed || 0) + (prevPh2_5?.carryForward || 0), prevPh2_5?.toPayWithPercentage || 0);
    const dispPago3_5 = Math.min((prevPh3_5?.payed || 0) + (prevPh3_5?.carryForward || 0), prevPh3_5?.toPayWithPercentage || 0);
    const dispPago4_5 = Math.min(prevPh4_5Effective, prevPh4_5?.toPayWithPercentage || 0);
    const totalPagoAnt5 = Number((dispPago1_5 + dispPago2_5 + dispPago3_5 + dispPago4_5).toFixed(2));
    const totalOrcamento5 = Number((totalPagoAnt5 + (p?.totalMissing || 0)).toFixed(2));
    const carryForwardFormula5 = p?.carryForward > 0
      ? <>{renderPrice(prevPh4_5Effective)} - {renderPrice(prevPh4_5?.toPayWithPercentage || 0)}</>
      : null;

    const previstoTooltip5 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('EXPECTED')} - {translate('PHASE')} 5 ({translate('FINAL_PHASE')})</div>
        <div style={{ marginBottom: 8 }}>
          {t('EXPECTED_DESC_FINAL_AFTER_3_4')}
        </div>
        <div style={{ marginBottom: 4, color: '#ddd', marginTop: 6 }}>{translate('PAID_BEFORE_THIS_PHASE')}:</div>
        {tooltipRow(`• ${phaseLabel(1)}`, renderPrice(dispPago1_5))}
        {tooltipRow(`• ${phaseLabel(2)}`, renderPrice(dispPago2_5))}
        {tooltipRow(`• ${phaseLabel(3)}`, renderPrice(dispPago3_5))}
        {tooltipRow(`• ${phaseLabel(4)}`, renderPrice(dispPago4_5))}
        {tooltipRow(t('ROW_TOTAL_PAID'), renderPrice(totalPagoAnt5), <>{renderPrice(dispPago1_5)} + {renderPrice(dispPago2_5)} + {renderPrice(dispPago3_5)} + {renderPrice(dispPago4_5)}</>)}
        {tooltipRow(t('ROW_OUTSTANDING_BASE'), renderPrice(p?.totalMissing || 0), <>{renderPrice(totalOrcamento5)} − {renderPrice(totalPagoAnt5)}</>)}
        {tooltipRow(t('ROW_VALUE_WITH_MARGIN'), renderPrice(p?.toPayWithPercentage || 0))}
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_ADJUST_PHASES_3_4')}
        </div>
      </div>
    );

    const pagoTooltip5 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('PAID')} - {translate('PHASE')} 5</div>
        <div style={{ marginBottom: 8 }}>
          {t('PAID_DESC_FINAL', { prev: phaseLabel(4) })}
        </div>
        {tooltipRow(t('ROW_CARRIED_FROM_PHASE', { phase: phaseLabel(4) }), renderPrice(p?.carryForward || 0), carryForwardFormula5)}
        {tooltipRow(t('ROW_PAYMENTS_THIS_PHASE'), renderPrice(p?.payed || 0))}
        {tooltipRow(t('ROW_EFFECTIVE_TOTAL'), renderPrice(effectivePago), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
      </div>
    );

    const faltaTooltip5 = (
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{translate('MISSING')} - {translate('PHASE')} 5</div>
        <div style={{ marginBottom: 8 }}>
          {t('MISSING_DESC_TOTAL')}
        </div>
        {tooltipRow(translate('EXPECTED'), renderPrice(p?.toPay || 0))}
        {tooltipRow(translate('CASH_PAYMENT'), renderPrice(effectivePago), <>{renderPrice(p?.payed || 0)} + {renderPrice(p?.carryForward || 0)}</>)}
        <div style={{ marginTop: 5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ color: '#ccc' }}>{translate('MISSING')}</span>
            <b>
              {missingAmount < 0 && '-'}{renderPrice(Math.abs(missingAmount))}
            </b>
          </div>
          <div style={{ fontSize: 10, color: 'inherit', marginTop: 1, textAlign: 'right' }}>
            {renderPrice(p?.toPay || 0)} - {renderPrice(effectivePago)}
          </div>
        </div>
        <div style={{ color: '#bbb', fontSize: 11, marginTop: 8, borderTop: '1px solid #555', paddingTop: 6 }}>
          {t('FOOTNOTE_COLOR_RULES_EXCESS')}
        </div>
      </div>
    );

    const previstoContent = <div><b>{renderPrice(p?.toPayWithPercentage || 0)}</b></div>;
    const pagoContent = (
      <>
        <div><b>{renderPrice(effectivePago)}</b></div>
        {p?.carryForward > 0 && (
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
            {translate('INCLUDES')} {renderPrice(p?.carryForward)} {translate('CARRIED_OVER')}
          </div>
        )}
      </>
    );
    const faltaContent = <div><b>{renderPrice(Math.max(0, missingAmount))}</b></div>;

    return renderPhaseValueGrid(
      previstoContent, pagoContent, faltaContent, missingColor,
      t('DISCLAIMER_ADJUST_PHASES_3_4'),
      prevComplete ? null : '100%',
      false,
      previstoTooltip5,
      pagoTooltip5,
      faltaTooltip5,
      null
    );
  };

  return (
    <React.Fragment>
      {paymentPhases.filter(p => !(p?.order > 4 && isPhase4Full)).map((p, index) => (
        <li key={'phase' + index} className='payment-phase line'>
          {renderPhaseTitle(p)}
          {!collapsedPhases.has(p?.order) && (
            <>
              {renderPhaseQuestions(p)}
              <div className='price'>
                {renderPhase1Values(p)}
                {renderPhase2Values(p)}
                {renderPhase3Values(p)}
                {renderPhase4Values(p)}
                {renderPhase5Values(p)}
              </div>
            </>
          )}
        </li>
      ))}
    </React.Fragment>
  );
};

export default PaymentPhasesBudget;
